// Copyright © 2026 Fannie Mae. All rights reserved.

import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Observable, catchError, of } from 'rxjs';
import { SllWorksheet } from './sll-calculation.models';
import { SllCalculationService } from './sll-calculation.service';

/** Returned by the resolver instead of a worksheet when find-or-create fails (UX-DR4/UX-DR13). */
export interface SllWorksheetResolveError {
  errorCode: string;
  errorMessage: string;
}

export type SllWorksheetResolveResult = SllWorksheet | SllWorksheetResolveError;

export function isSllWorksheetResolveError(
  result: SllWorksheetResolveResult
): result is SllWorksheetResolveError {
  return (result as SllWorksheetResolveError).errorCode !== undefined;
}

/**
 * Resolves/creates the SLL worksheet for the `/sll/lenders/{lenderId}/periods/{financialPeriod}`
 * route (design.md §3.2/§3.3). The optional `clsReviewId` query param arrives from the CLS entry
 * link — the route path identifies the business object, but this resolver never trusts the
 * client for anything beyond entry context; the server independently resolves the framework type
 * and validates/persists the authoritative worksheet (Step 2 — `frameworkType` is no longer a
 * client-supplied query param).
 */
export const sllWorksheetResolver: ResolveFn<SllWorksheetResolveResult> = (
  route
): Observable<SllWorksheetResolveResult> => {
  const service = inject(SllCalculationService);

  const lenderIdParam = route.paramMap.get('lenderId');
  const financialPeriod = route.paramMap.get('financialPeriod') ?? '';
  const clsReviewIdParam = route.queryParamMap.get('clsReviewId');

  const lenderId = lenderIdParam ? Number(lenderIdParam) : NaN;

  if (!lenderIdParam || Number.isNaN(lenderId) || !financialPeriod) {
    return of({
      errorCode: 'INVALID_WORKSHEET_REQUEST',
      errorMessage: 'Missing or invalid lender or financial period context.'
    });
  }

  return service
    .resolveOrCreate({
      lenderId,
      financialPeriod,
      clsReviewId: clsReviewIdParam ? Number(clsReviewIdParam) : null
    })
    .pipe(
      catchError((err: HttpErrorResponse) => {
        const body = err.error as { code?: string; message?: string } | null;
        return of({
          errorCode: body?.code ?? 'UNKNOWN_ERROR',
          errorMessage: body?.message ?? 'Unable to resolve or create the SLL worksheet.'
        });
      })
    );
};
